import React, { useState } from 'react';
import Header from '../../components/Header';
import { AiOutlineUpload, AiOutlineClose } from 'react-icons/ai';
import { FaUsers } from 'react-icons/fa6';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/Auth';
import { toast } from 'react-toastify';

const NewGroup = () => {
  const { requestWithAuth } = useAuth();
  const [formData, setFormData] = useState({
    group_name: '',
    group_category: '',
    group_logo: null
  });
  
  const [logoPreview, setLogoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          group_logo: 'Please select a valid image file (JPEG, PNG, GIF)'
        }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          group_logo: 'File size must be less than 5MB'
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        group_logo: file
      }));

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);

      // Clear any existing error
      setErrors(prev => ({
        ...prev,
        group_logo: ''
      }));
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({
      ...prev,
      group_logo: null
    }));
    setLogoPreview(null);
    // Reset file input
    const fileInput = document.getElementById('group_logo');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.group_name.trim()) {
      newErrors.group_name = 'Group name is required';
    }

    if (!formData.group_category) {
      newErrors.group_category = 'Please select a group category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('group_name', formData.group_name);
      submitData.append('group_category', formData.group_category);
      
      if (formData.group_logo) {
        submitData.append('group_logo', formData.group_logo);
      }

      // TODO: Replace with your actual API call
      const responseData = await requestWithAuth(
        "/groups/church-groups/",
        "POST",
        formData,
        {
          "Content-Type": "multipart/form-data",
        }
      )
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Group has been created successfully",
      })
      
      // Reset form
      setFormData({
        group_name: '',
        group_category: '',
        group_logo: null
      });
      setLogoPreview(null);
      
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-6">
      <Header title="Add New Group" />
      
      <div className="max-w-2xl mx-auto mt-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Form Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaUsers className="text-blue-600 text-xl" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Create New Group</h2>
                <p className="text-sm text-gray-600">Fill in the details to create a new group</p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Group Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Logo
                <span className="text-gray-500 text-xs ml-1">(Optional)</span>
              </label>
              
              {logoPreview ? (
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Group logo preview"
                      className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                    >
                      <AiOutlineClose className="text-sm" />
                    </button>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-2">Logo uploaded successfully</p>
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove logo
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <AiOutlineUpload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <label htmlFor="group_logo" className="font-medium text-blue-600 hover:text-blue-500 cursor-pointer">
                        Click to upload
                      </label>
                      {' '}or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                  </div>
                  <input
                    id="group_logo"
                    name="group_logo"
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </div>
              )}
              
              {errors.group_logo && (
                <p className="mt-2 text-sm text-red-600">{errors.group_logo}</p>
              )}
            </div>

            {/* Group Name */}
            <div>
              <label htmlFor="group_name" className="block text-sm font-medium text-gray-700 mb-2">
                Group Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="group_name"
                name="group_name"
                value={formData.group_name}
                onChange={handleInputChange}
                placeholder="Enter group name"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                  errors.group_name 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300'
                }`}
              />
              {errors.group_name && (
                <p className="mt-2 text-sm text-red-600">{errors.group_name}</p>
              )}
            </div>

            {/* Group Category */}
            <div>
              <label htmlFor="group_category" className="block text-sm font-medium text-gray-700 mb-2">
                Group Category
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                id="group_category"
                name="group_category"
                value={formData.group_category}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                  errors.group_category 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300'
                }`}
              >
                <option value="">Select a category</option>
                <option value="Generational">Generational</option>
                <option value="Service">Service</option>
              </select>
              {errors.group_category && (
                <p className="mt-2 text-sm text-red-600">{errors.group_category}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <FaUsers className="text-sm" />
                    Create Group
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewGroup;